import { AdminRole } from "@repo/api-dtos"
import {
	BadgePercent,
	Ban,
	Box,
	Boxes,
	FerrisWheel,
	Landmark,
	Mails,
	Plug,
	Plus,
	Shield,
	Ticket,
	TicketPercent,
} from "lucide-react"
import {
	COUPON_NEW_SEARCH_DEFAULTS,
	EXPANSION_GROUPS_SEARCH_DEFAULTS,
} from "@/config/search-defaults"
import type { PaletteCommand } from "../types"
import { gamePickerStep } from "./game-picker"

const CATALOGUE_WRITE = [AdminRole.CatalogueWrite, AdminRole.CatalogueDeploy]

export function buildCreateCommands(): PaletteCommand[] {
	return [
		{
			id: "create:coupon",
			label: "New coupon",
			icon: Ticket,
			group: "create",
			requiredRoles: [AdminRole.MarketplaceCoupons],
			perform: (ctx) => {
				ctx.navigate({ to: "/coupons/new", search: COUPON_NEW_SEARCH_DEFAULTS })
				ctx.close()
			},
		},
		{
			id: "create:coupon-program",
			label: "New coupon program",
			icon: Mails,
			group: "create",
			requiredRoles: [AdminRole.MarketplaceCoupons],
			perform: (ctx) => {
				ctx.navigate({ to: "/coupons/programs/new" })
				ctx.close()
			},
		},
		{
			id: "create:seller-promotion",
			label: "New seller promotion",
			icon: BadgePercent,
			group: "create",
			requiredRoles: [AdminRole.MarketplaceFinance],
			perform: (ctx) => {
				ctx.navigate({ to: "/seller-promotions/new" })
				ctx.close()
			},
		},
		{
			id: "create:buyer-promotion",
			label: "New buyer promotion",
			icon: TicketPercent,
			group: "create",
			requiredRoles: [AdminRole.MarketplaceCoupons],
			perform: (ctx) => {
				ctx.navigate({ to: "/buyer-promotions/new" })
				ctx.close()
			},
		},
		{
			id: "create:live-event",
			label: "New live event",
			icon: FerrisWheel,
			group: "create",
			requiredRoles: [AdminRole.EventsManage],
			perform: (ctx) => {
				ctx.navigate({ to: "/live-events/new" })
				ctx.close()
			},
		},
		{
			id: "create:integration-app",
			label: "New integration app",
			icon: Plug,
			group: "create",
			requiredRoles: [AdminRole.IntegrationsAdmin],
			perform: (ctx) => {
				ctx.navigate({ to: "/integration-apps/new" })
				ctx.close()
			},
		},
		{
			id: "create:expansion",
			label: "New expansion…",
			icon: Box,
			group: "create",
			requiredRoles: CATALOGUE_WRITE,
			perform: async (ctx) => {
				ctx.push(
					await gamePickerStep(ctx, "New expansion in", (game) => ({
						perform: (c) => {
							c.navigate({
								to: "/catalogue/games/$gameSlug",
								params: { gameSlug: game.gameSlug },
								search: { create: "expansion" },
							})
							c.close()
						},
					})),
				)
			},
		},
		{
			id: "create:product-card",
			label: "New product (card)…",
			icon: Plus,
			group: "create",
			requiredRoles: CATALOGUE_WRITE,
			perform: async (ctx) => {
				ctx.push(
					await gamePickerStep(ctx, "New card in", (game) => ({
						perform: (c) => {
							c.navigate({
								to: "/catalogue/games/$gameSlug",
								params: { gameSlug: game.gameSlug },
								search: { create: "product-card" },
							})
							c.close()
						},
					})),
				)
			},
		},
		{
			id: "create:product-sealed",
			label: "New product (sealed)…",
			icon: Plus,
			group: "create",
			requiredRoles: CATALOGUE_WRITE,
			perform: async (ctx) => {
				ctx.push(
					await gamePickerStep(ctx, "New sealed product in", (game) => ({
						perform: (c) => {
							c.navigate({
								to: "/catalogue/games/$gameSlug",
								params: { gameSlug: game.gameSlug },
								search: { create: "product-sealed" },
							})
							c.close()
						},
					})),
				)
			},
		},
		{
			id: "create:expansion-group",
			label: "New expansion group…",
			icon: Boxes,
			group: "create",
			requiredRoles: CATALOGUE_WRITE,
			perform: async (ctx) => {
				ctx.push(
					await gamePickerStep(ctx, "New expansion group in", (game) => ({
						perform: (c) => {
							c.navigate({
								to: "/expansion-groups",
								search: {
									...EXPANSION_GROUPS_SEARCH_DEFAULTS,
									gameSlug: game.gameSlug,
									create: true,
								},
							})
							c.close()
						},
					})),
				)
			},
		},
		{
			id: "create:ip-ban",
			label: "Add IP ban",
			icon: Ban,
			group: "create",
			requiredRoles: [AdminRole.MarketplaceUserManagement],
			perform: (ctx) => {
				ctx.navigate({ to: "/moderation/ip-bans", search: { add: true } })
				ctx.close()
			},
		},
		{
			id: "create:admin-user",
			label: "Add admin user",
			icon: Shield,
			group: "create",
			requiredRoles: [AdminRole.Superadmin],
			perform: (ctx) => {
				ctx.navigate({ to: "/admin-users", search: { add: true } })
				ctx.close()
			},
		},
		{
			id: "create:vat-filing",
			label: "Log VAT filing",
			icon: Landmark,
			group: "create",
			requiredRoles: [AdminRole.MarketplaceFinance],
			perform: (ctx) => {
				ctx.navigate({ to: "/accounting/vat-remittances" })
				ctx.close()
				setTimeout(() => {
					document
						.getElementById("log-filing")
						?.scrollIntoView({ behavior: "smooth" })
				}, 300)
			},
		},
	]
}
